<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import ConfigForm from '../components/ConfigForm.vue';
import { createConfigDraft } from '../components/configDraft';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import StudioSection from '../components/StudioSection.vue';
import StudioBadge from '../components/StudioBadge.vue';
import StudioEmptyState from '../components/StudioEmptyState.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const configState = createConfigDraft(props.bot);
const { setValue } = configState;

const fields = [
  {
    path: 'badword.enabled',
    label: 'AI_AGENT.BADWORDS.FIELDS.ENABLED',
    type: 'switch',
  },
  {
    path: 'badword.action',
    label: 'AI_AGENT.BADWORDS.FIELDS.ACTION',
    type: 'select',
    options: [
      {
        value: 'warn',
        label: t('AI_AGENT.BADWORDS.FIELDS.ACTION_WARN'),
      },
      {
        value: 'ignore',
        label: t('AI_AGENT.BADWORDS.FIELDS.ACTION_IGNORE'),
      },
    ],
  },
  {
    path: 'badword.normalize_leetspeak',
    label: 'AI_AGENT.BADWORDS.FIELDS.NORMALIZE_LEETSPEAK',
    type: 'switch',
  },
];

const records = useMapGetter('aiBotBadwords/getRecords');
const uiFlags = useMapGetter('aiBotBadwords/getUIFlags');

const newWord = ref('');
const newWordIsRegex = ref(false);

const addWord = async () => {
  const word = newWord.value.trim();
  if (!word) return;
  const saved = await store.dispatch('aiBotBadwords/create', {
    botId: props.bot.id,
    word,
    is_regex: newWordIsRegex.value,
  });
  if (saved) {
    newWord.value = '';
    newWordIsRegex.value = false;
  }
};

const toggleEnabled = record => {
  store.dispatch('aiBotBadwords/update', {
    botId: props.bot.id,
    id: record.id,
    enabled: !record.enabled,
  });
};

const removeWord = record => {
  store.dispatch('aiBotBadwords/delete', {
    botId: props.bot.id,
    id: record.id,
  });
};

// ---- bulk import (one word per line) ----------------------------------------
const bulkText = ref('');
const bulkIsRegex = ref(false);
const lastBulkCount = ref(null);

const submitBulk = async () => {
  const saved = await store.dispatch('aiBotBadwords/bulk', {
    botId: props.bot.id,
    words: bulkText.value,
    is_regex: bulkIsRegex.value,
  });
  if (saved) {
    lastBulkCount.value = saved.created;
    bulkText.value = '';
    store.dispatch('aiBotBadwords/get', { botId: props.bot.id });
  }
};

// ---- string-list editor (exempt words) ------------------------------------------
// badword.exempt_words is a string array in bot_config;
// edit them as one-per-line text areas and write back trimmed non-empty lines.
const listText = key =>
  computed({
    get: () => {
      const value = props.bot.bot_config?.badword?.[key];
      return Array.isArray(value) ? value.join('\n') : '';
    },
    set: value => {
      setValue(
        { path: `badword.${key}` },
        value
          .split('\n')
          .map(item => item.trim())
          .filter(item => item !== '')
      );
    },
  });

const exemptWordsText = listText('exempt_words');

onMounted(() => {
  store.dispatch('aiBotBadwords/get', { botId: props.bot.id });
});
</script>

<template>
  <div class="flex flex-col gap-5">
    <StudioSection
      :title="$t('AI_AGENT.BADWORDS.WORDS.HEADING')"
      :description="$t('AI_AGENT.BADWORDS.WORDS.HELP')"
      icon="i-lucide-list-x"
    >
      <template #actions>
        <StudioBadge>
          {{ $t('AI_AGENT.BADWORDS.WORDS.COUNT', { n: records.length }) }}
        </StudioBadge>
      </template>

      <div
        class="flex flex-col gap-2 p-2 rounded-xl sm:flex-row sm:items-center bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
      >
        <div class="flex-1 min-w-0">
          <Input
            id="badword-new-word"
            v-model="newWord"
            :placeholder="$t('AI_AGENT.BADWORDS.WORDS.ADD_PLACEHOLDER')"
            @enter="addWord"
          />
        </div>
        <div class="flex items-center justify-between gap-3 sm:justify-end">
          <label
            class="flex items-center gap-2 text-label-small text-n-slate-11"
          >
            <Switch
              :model-value="newWordIsRegex"
              data-testid="badword-new-is-regex"
              @update:model-value="newWordIsRegex = $event"
            />
            {{ $t('AI_AGENT.BADWORDS.WORDS.REGEX') }}
          </label>
          <Button
            icon="i-lucide-plus"
            sm
            :label="$t('AI_AGENT.BADWORDS.WORDS.ADD')"
            :is-loading="uiFlags.isCreating"
            :disabled="!newWord.trim()"
            @click="addWord"
          />
        </div>
      </div>

      <StudioEmptyState
        v-if="!records.length"
        icon="i-lucide-shield-check"
        :title="$t('AI_AGENT.BADWORDS.WORDS.EMPTY')"
        :description="$t('AI_AGENT.BADWORDS.WORDS.EMPTY_HELP')"
        data-testid="badword-empty"
      />
      <ul v-else class="flex flex-col divide-y divide-n-weak">
        <li
          v-for="record in records"
          :key="record.id"
          class="flex items-center gap-3 py-2.5 transition-opacity motion-reduce:transition-none"
          :class="{ 'opacity-60': !record.enabled }"
        >
          <code
            class="px-2 py-0.5 text-sm rounded-md truncate bg-n-alpha-2 text-n-slate-12"
          >
            {{ record.word }}
          </code>
          <StudioBadge v-if="record.is_regex" tone="amber">
            {{ $t('AI_AGENT.BADWORDS.WORDS.REGEX') }}
          </StudioBadge>
          <div class="flex items-center gap-2 ml-auto">
            <Switch
              :model-value="record.enabled"
              :data-testid="`badword-toggle-${record.id}`"
              @update:model-value="toggleEnabled(record)"
            />
            <Button
              v-tooltip.top="$t('AI_AGENT.BADWORDS.WORDS.DELETE')"
              icon="i-woot-bin"
              slate
              ghost
              xs
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              :data-testid="`badword-delete-${record.id}`"
              @click="removeWord(record)"
            />
          </div>
        </li>
      </ul>

      <details
        class="group rounded-xl bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
      >
        <summary
          class="flex items-center gap-2 px-3 py-2.5 list-none cursor-pointer select-none text-label text-n-slate-12 [&::-webkit-details-marker]:hidden"
        >
          <span
            class="transition-transform i-lucide-chevron-right size-4 text-n-slate-10 group-open:rotate-90 motion-reduce:transition-none"
          />
          <span class="i-lucide-clipboard-list size-4 text-n-slate-10" />
          {{ $t('AI_AGENT.BADWORDS.WORDS.BULK_HEADING') }}
        </summary>
        <div class="flex flex-col gap-3 px-3 pb-3">
          <p class="text-label-small text-n-slate-10">
            {{ $t('AI_AGENT.BADWORDS.WORDS.BULK_HELP') }}
          </p>
          <TextArea
            id="badword-bulk"
            v-model="bulkText"
            :placeholder="$t('AI_AGENT.BADWORDS.WORDS.BULK_PLACEHOLDER')"
            auto-height
          />
          <div class="flex flex-wrap items-center gap-3">
            <label
              class="flex items-center gap-2 text-label-small text-n-slate-11"
            >
              <Switch
                :model-value="bulkIsRegex"
                data-testid="badword-bulk-is-regex"
                @update:model-value="bulkIsRegex = $event"
              />
              {{ $t('AI_AGENT.BADWORDS.WORDS.REGEX') }}
            </label>
            <Button
              faded
              slate
              sm
              icon="i-lucide-upload"
              :label="$t('AI_AGENT.BADWORDS.WORDS.BULK_SUBMIT')"
              :is-loading="uiFlags.isBulkImporting"
              :disabled="!bulkText.trim()"
              @click="submitBulk"
            />
            <StudioBadge
              v-if="lastBulkCount !== null"
              tone="teal"
              data-testid="badword-bulk-result"
            >
              {{
                $t('AI_AGENT.BADWORDS.WORDS.BULK_RESULT', { n: lastBulkCount })
              }}
            </StudioBadge>
          </div>
        </div>
      </details>
    </StudioSection>

    <ConfigForm
      :bot="bot"
      :state="configState"
      :fields="fields"
      :title="$t('AI_AGENT.BADWORDS.SECTIONS.SETTINGS')"
      :description="$t('AI_AGENT.BADWORDS.SECTIONS.SETTINGS_HELP')"
      icon="i-lucide-sliders-horizontal"
    >
      <div class="grid grid-cols-1 gap-5">
        <StudioSection
          :title="$t('AI_AGENT.BADWORDS.EXEMPT_WORDS.HEADING')"
          :description="$t('AI_AGENT.BADWORDS.EXEMPT_WORDS.HELP')"
          icon="i-lucide-badge-check"
        >
          <TextArea
            id="badword-exempt-words"
            v-model="exemptWordsText"
            :placeholder="$t('AI_AGENT.BADWORDS.EXEMPT_WORDS.PLACEHOLDER')"
            data-testid="field-badword.exempt_words"
            auto-height
          />
        </StudioSection>
      </div>
    </ConfigForm>
  </div>
</template>
